// snag.js: the one script tag a project adds (README › Adding Snag to a project). Small and inert: it waits for a way
// in, then imports picker.js from Snag (the picker, the drawer and the screenshot library) and starts it. Visitors who
// never use it download nothing else; one who does can pick, but filing needs a super admin's sign-in.
//
//   <script src="https://snag.gowebsitedev.com/snag.js" data-project="rankhint" async></script>
//
// Ways in: Alt+Shift+S (⌥⇧S); `#snag` at the end of the page's address (phones, the first time); the phone button,
// once this browser has signed in to Snag on this site; `snag.pick()` / `snag.open()` from the page's own menu.
//
// data-project: the GitHub repo issues go to (under GITHUB_OWNER). Optional: data-build (a version or commit, put in
// the issue), data-key="off" (no shortcut and no phone button: only the page's calls start it). A single-page app can
// set `window.snag.route = () => "/sites/$siteId"` so issues name the route as its router writes it.
//
// Pages in a frame (README): a sandboxed iframe can't hold the drawer. The page around it loads snag.js as usual and
// marks the iframe `data-snag-frame`; the page in the frame loads it with `data-relay="<the outer page's origin>"`,
// picks in there, and the outer page's drawer files it.

import { PROJECT } from "../shared.ts";
import type { HostOptions } from "./host.ts";
import { isSnagKey } from "./keys.ts";

interface Snag {
  /** start the picker (the page's own "Report an issue", say) */
  pick(): void;
  /** open the drawer with nothing picked */
  open(): void;
  /** the route, as the page's router names it; the path by default */
  route?: () => string;
}

declare global {
  interface Window {
    snag?: Snag;
  }
}

/** mirrors ADMIN_HINT in host.ts and MSG.relayPick in shared.ts (not imported: this file stays small) */
const ADMIN_HINT = "snag-admin";
const RELAY_PICK = "snag:relay-pick";

(() => {
  const script = document.currentScript as HTMLScriptElement | null;
  if (!script?.src || window.snag) return;
  const origin = new URL(script.src).origin;
  const project = script.dataset.project ?? "";
  if (!PROJECT.test(project)) {
    console.warn('snag: add data-project="<repo name>" to the script tag');
    return;
  }

  let relay: string | undefined;
  try {
    relay = script.dataset.relay ? new URL(script.dataset.relay).origin : undefined;
  } catch {
    console.warn('snag: data-relay should be the outer page\'s origin, e.g. data-relay="https://example.com"');
    return;
  }

  let loading = false;
  const load = (then: "pick" | "open" | "host") => {
    if (loading) return;
    loading = true;
    import(`${origin}/picker.js`)
      .then((m: Record<"pick" | "open" | "host", (o: HostOptions) => void>) =>
        m[then]({ origin, project, build: script.dataset.build ?? "", route: () => snag.route?.() || location.pathname, relay }),
      )
      .catch((e: unknown) => console.warn("snag: it didn't load (a Content-Security-Policy that leaves out Snag?)", e))
      .finally(() => (loading = false));
  };
  const snag: Snag = { pick: () => load("pick"), open: () => load("open") };
  window.snag = snag;

  const whenReady = (fn: () => void) => (document.readyState === "loading" ? document.addEventListener("DOMContentLoaded", fn, { once: true }) : fn());

  if (relay) {
    // the outer page: "pick" when its shortcut, phone button or Pick again is pressed
    window.addEventListener("message", (e) => {
      if (e.source === window.parent && e.origin === relay && (e.data as { type?: string } | null)?.type === RELAY_PICK) snag.pick();
    });
  } else {
    // a frame picks for this page: the drawer is ready before anything is picked in there
    whenReady(() => {
      if (document.querySelector("iframe[data-snag-frame]")) load("host");
    });
  }

  if (script.dataset.key === "off") return;
  // capture: a page that stops keys on their way still lets Snag see this one
  window.addEventListener(
    "keydown",
    (e) => {
      if (!isSnagKey(e) || e.repeat || e.isComposing) return;
      e.preventDefault();
      e.stopPropagation();
      snag.pick();
    },
    { capture: true },
  );

  // page.html#snag: phones have no shortcut. The hash comes off, so a reload or a shared link doesn't start it again.
  const fromHash = () => {
    if (location.hash !== "#snag") return;
    history.replaceState(history.state, "", location.pathname + location.search);
    whenReady(() => snag.pick());
  };
  fromHash();
  window.addEventListener("hashchange", fromHash);

  // the phone button: on touch screens, once this browser has signed in to Snag on this site (the drawer leaves a
  // note); everyone else never sees it
  let signedInHere = false;
  try {
    signedInHere = localStorage.getItem(ADMIN_HINT) === "1";
  } catch {
    // storage blocked: #snag still works
  }
  if (signedInHere && !relay && matchMedia("(pointer: coarse)").matches) whenReady(() => phoneButton(snag));
})();

function phoneButton(snag: Snag) {
  const host = document.createElement("div");
  host.setAttribute("data-snag", "");
  const root = host.attachShadow({ mode: "closed" });
  // a constructed sheet: a page's Content-Security-Policy can refuse a <style>, never this
  const sheet = new CSSStyleSheet();
  sheet.replaceSync(`button { position: fixed; left: 14px; bottom: calc(14px + env(safe-area-inset-bottom)); z-index: 2147483645; width: 44px; height: 44px;
  display: grid; place-items: center; padding: 0; border: 0; border-radius: 50%; background: #111827; opacity: .82; cursor: pointer;
  box-shadow: 0 4px 14px rgba(0,0,0,.25); -webkit-tap-highlight-color: transparent; }
button:active { opacity: 1; transform: scale(.96); }`);
  root.adoptedStyleSheets = [sheet];
  root.innerHTML = `<button type="button" aria-label="Report an issue with Snag" title="Report an issue (Snag)"><svg width="22" height="22" viewBox="0 0 32 32" aria-hidden="true"><path d="M8 8h6M8 8v6M24 8h-6M24 8v6M8 24h6M8 24v-6M24 24h-6M24 24v-6" stroke="#fff" stroke-width="2.5" stroke-linecap="round" fill="none"/><circle cx="16" cy="16" r="2.5" fill="#fff"/></svg></button>`;
  root.querySelector("button")!.addEventListener("click", () => snag.pick());
  document.body.append(host);
}
